import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TablePagination } from '@mui/material';
import { TFunction } from 'i18next';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import {
  ALL_FIELDS,
  ASC_ORDER,
  CAPITAL_ASC,
  ERROR,
  FIRST_NAME,
  DEPARTMENT,
  LAST_NAME,
  SUPPLIER_ID,
  SUPPLIER_NAME_FIELD,
  USER_CODE,
  USER_ID,
} from '@/helpers/constants';
import useApiRequest from '@/hooks/useApiRequest';
import useToastContext from '@/hooks/useToastContext';
import usersServices from '@/services/usersServices';
import { ColumnSorting, UserSearch } from '@/types';
import { Filter } from '@/types/TableListEditable';
import {
  defaultFilter,
  defaultFilterValues,
  tableColumns,
} from '../SearchTable/search-table-utils';
import UsersTableHeader from '../SearchTable/UsersTableHeader/UsersTableHeader';
import { DEFAULT_FILTERS, FILTER_TYPES, PAGE_SIZES, TABLE_ROW_SELECTION } from '../UI/TableListEditable/TableListEditableConstants';

export type SupplierResult = {
  id: string;
  name?: string;
  addressCity?: string;
  addressCountry?: string;
  addressPostalCode?: string;
  addressStreet?: string;
};

type DialogSearchSelection = UserSearch[] | UserSearch | SupplierResult | string | null;

type UserResult = UserSearch & {
  userCode: string;
  lastName?: string;
  firstName?: string;
  department?: string;
  selected?: boolean;
};

type RemoteDisplayRow = {
  userCode?: string;
  supplierId?: string;
  supplierName?: string;
  lastName?: string;
  firstName?: string;
  department?: string;
};

type RemoteSearchResponse = {
  results?: Array<UserResult | SupplierResult>;
  totalPages?: number;
  pageSize?: number;
  pageNumber?: number;
};

interface DialogSearchRemoteTableProps {
  isSupplier?: boolean;
  defaultValue?: string;
  level?: number;
  isFixedLevel?: boolean;
  plant?: string;
  authorizedPlants?: boolean;
  setSelectedOption: (selectedOption: DialogSearchSelection) => void;
  setSaveDisabled: (disabled: boolean) => void;
}

const mapColumn = (columnName: string, isSupplier: boolean) => (!isSupplier && columnName === USER_CODE ? USER_ID : columnName);

const serializeComparable = (value: unknown) => JSON.stringify(value);

const areEqualBySerialization = <T,>(left: T, right: T) => serializeComparable(left) === serializeComparable(right);

const getDefaultSorting = (isSupplier: boolean): ColumnSorting[] => ([{ columnName: isSupplier ? SUPPLIER_ID : LAST_NAME, direction: ASC_ORDER }]);

const mapSupplierRows = (result: SupplierResult[] = []) => result.map((row) => {
  const { id, addressCity, addressCountry, addressPostalCode, addressStreet, name } = row;

  const supplierName = [name, addressCountry, addressPostalCode, addressCity, addressStreet]
    .filter((text) => typeof text === 'string' && text.trim() !== '')
    .join(', ');

  return { [SUPPLIER_ID]: id, [SUPPLIER_NAME_FIELD]: supplierName };
});

const createFilterModel = (filters: Filter[]) => filters.reduce<Record<string, unknown>>((acc, filter) => {
  if (!filter?.columnName || filter.value === '') return acc;

  return {
    ...acc,
    [filter.columnName]: {
      filterType: FILTER_TYPES.TEXT,
      type: DEFAULT_FILTERS[0],
      filter: filter.value,
    },
  };
}, {});

const createFilterArray = (filterModel: Record<string, unknown>, preservedAllFields?: Filter) => {
  const modelEntries = Object.entries(filterModel || {}).reduce<Filter[]>((acc, [columnName, config]) => {
    const value = (config as { filter?: string })?.filter ?? '';

    if (value === '') return acc;

    return [...acc, { columnName, value, operation: DEFAULT_FILTERS[0] }];
  }, []);

  return preservedAllFields?.value ? [preservedAllFields, ...modelEntries] : modelEntries;
};

const getPageInfoLabel = (page: number, totalCount: number, pageSize: number, t: TFunction) => {
  if (!totalCount) return '';

  return t('dialog.page', { page: page + 1, total: Math.max(1, Math.ceil(totalCount / pageSize)) });
};

const DialogSearchRemoteTable: React.FC<DialogSearchRemoteTableProps> = (props) => {
  const {
    isSupplier = false,
    defaultValue,
    level,
    isFixedLevel,
    plant,
    authorizedPlants = false,
    setSelectedOption,
    setSaveDisabled,
  } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [rows, setRows] = useState<RemoteDisplayRow[]>([]);
  const [results, setResults] = useState<Array<UserResult | SupplierResult>>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filter[]>(defaultFilterValues(!!isSupplier, defaultValue || '', false, false));
  const [sorting, setSorting] = useState<ColumnSorting[]>(getDefaultSorting(isSupplier));
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [defaultSelection, setDefaultSelection] = useState(defaultValue || '');
  const [firstRender, setFirstRender] = useState(true);
  const { columns } = useMemo(() => tableColumns(t, !!isSupplier, false, false), [isSupplier, t]);
  const filterModel = useMemo(() => createFilterModel(filters), [filters]);
  const fetchSuppliers = useApiRequest(usersServices.getSuppliers);
  const fetchUsersFilterTable = useApiRequest(usersServices.getUsersFilterTable);
  const fetchUsersLevelFilterTable = useApiRequest(usersServices.getUsersLevelFilterTable);

  const syncSelectionWithRows = (displayRows: RemoteDisplayRow[]) => {
    const availableRowIds = displayRows.map((row) => row[SUPPLIER_ID] || row[USER_CODE]).filter(Boolean);
    const nextSelection = selection.filter((selectionValue) => availableRowIds.includes(selectionValue as string));

    if (nextSelection.length > 0) {
      setSelection(nextSelection);

      return;
    }

    if (defaultSelection && availableRowIds.includes(defaultSelection)) {
      setSelection([defaultSelection]);

      return;
    }

    setSelection([]);
  };

  const buildRequestFilters = (filterList: Filter[]) => filterList.reduce<Record<string, string>>((acc, filter) => {
    if (!filter?.columnName) return acc;

    return {
      ...acc,
      [mapColumn(filter.columnName, !!isSupplier)]: String(filter.value ?? ''),
    };
  }, {
    ...(defaultFilter(!!isSupplier) as unknown as Record<string, string>),
    ...(plant ? { plant } : {}),
    ...(authorizedPlants ? { authorizedPlants: String(authorizedPlants) } : {}),
  });

  const getSupplierRows = (responseResults: SupplierResult[], keepFirstRenderSelection: boolean) => {
    const index = responseResults.findIndex(({ id }) => id === defaultValue);

    return mapSupplierRows(keepFirstRenderSelection && defaultValue && index !== -1 ? [responseResults[index]] : responseResults);
  };

  const loadData = async (keepFirstRenderSelection: boolean) => {
    setLoading(true);

    const serviceFilters = buildRequestFilters(filters);
    const [sortColumn] = sorting;
    const sortDirection = sortColumn?.direction?.toUpperCase() || CAPITAL_ASC;
    const sortName = mapColumn(sortColumn?.columnName || (isSupplier ? SUPPLIER_ID : LAST_NAME), !!isSupplier);

    try {
      let response: RemoteSearchResponse | undefined;

      if (isSupplier) {
        response = await fetchSuppliers(serviceFilters, currentPage, pageSize);
      } else if (level) {
        response = await fetchUsersLevelFilterTable(level, serviceFilters, currentPage, pageSize, sortName, sortDirection, isFixedLevel);
      } else {
        response = await fetchUsersFilterTable(serviceFilters, currentPage, pageSize, sortName, sortDirection);
      }

      if (!response) return;

      const nextResults = response.results || [];
      const nextRows = isSupplier
        ? getSupplierRows(nextResults as SupplierResult[], keepFirstRenderSelection)
        : nextResults as UserResult[];

      setResults(nextResults);
      setRows(nextRows as RemoteDisplayRow[]);
      setTotalCount(isSupplier && keepFirstRenderSelection && defaultValue ? 1 : (response.totalPages || 0) * (response.pageSize || pageSize));
      setCurrentPage(response.pageNumber || 0);
      setPageSize(response.pageSize || pageSize);
      syncSelectionWithRows(nextRows as RemoteDisplayRow[]);
      setFirstRender(false);
    } catch (error) {
      if ((error as { message?: string })?.message) {
        addToast((error as { message: string }).message, ERROR);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(firstRender);
  }, [filters, sorting, currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const findSelectedResult = (selectedValue?: string | number) => {
    if (selectedValue === undefined) return null;

    return results.find((row) => (isSupplier ? (row as SupplierResult).id === selectedValue : (row as UserResult)[USER_CODE] === selectedValue)) || null;
  };

  const handleSelectionChange = (newSelection: unknown[]) => {
    const selectedValue = newSelection[0] as string | number | undefined;
    const nextSelection = selectedValue !== undefined ? [String(selectedValue)] : [];

    setSelection(nextSelection);

    if (isSupplier) {
      const supplierSelected = findSelectedResult(selectedValue) as SupplierResult | null;

      setDefaultSelection(supplierSelected?.id || '');
      setSelectedOption(supplierSelected as SupplierResult | null);
      setSaveDisabled(!supplierSelected);

      return;
    }

    const userSelected = selectedValue
      ? { ...(findSelectedResult(selectedValue) as UserResult), selected: true }
      : { [LAST_NAME]: '', [FIRST_NAME]: '', [DEPARTMENT]: '', selected: false };

    setDefaultSelection(userSelected.selected ? userSelected[USER_CODE] : '');
    setSelectedOption(userSelected as UserSearch);
    setSaveDisabled(!userSelected.selected);
  };

  const handleHeaderFilterChange = (newFilters: Filter[]) => {
    if (areEqualBySerialization(newFilters, filters)) return;

    setCurrentPage(0);
    setFilters(newFilters);
  };

  const handleGridFilterChange = (newFilterModel: Record<string, unknown>) => {
    const allFieldsFilter = filters.find((filter) => filter.columnName === ALL_FIELDS);
    const nextFilters = createFilterArray(newFilterModel, allFieldsFilter);

    if (areEqualBySerialization(nextFilters, filters)) return;

    setCurrentPage(0);
    setFilters(nextFilters);
  };

  const handleSortModelChange = (newSorting: ColumnSorting[]) => {
    const nextSorting = newSorting.length > 0 ? newSorting : getDefaultSorting(isSupplier);

    if (areEqualBySerialization(nextSorting, sorting)) return;

    setCurrentPage(0);
    setSorting(nextSorting);
  };

  return (
    <>
      <UsersTableHeader filters={filters} handleFilterChange={handleHeaderFilterChange} />
      <TableListEditableNew<RemoteDisplayRow>
        isFiltering
        hasCheckbox
        isPaginated={false}
        list={rows}
        columns={columns}
        columnId={isSupplier ? SUPPLIER_ID : USER_CODE}
        selection={selection}
        rowSelectionMode={TABLE_ROW_SELECTION.SINGLE}
        onSelectionChange={handleSelectionChange}
        loading={loading}
        filterModel={filterModel}
        onFilterModelChange={handleGridFilterChange}
        columnSorting={sorting}
        onSortModelChange={handleSortModelChange}
      />
      <TablePagination
        component="div"
        count={totalCount}
        page={currentPage}
        onPageChange={(_, page) => setCurrentPage(page)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => {
          setCurrentPage(0);
          setPageSize(parseInt(event.target.value, 10));
        }}
        rowsPerPageOptions={PAGE_SIZES}
        labelRowsPerPage={t('dialog.rowsPerPage')}
        labelDisplayedRows={() => getPageInfoLabel(currentPage, totalCount, pageSize, t)}
      />
    </>
  );
};

export default DialogSearchRemoteTable;
